/**
 * Format a number as Chilean Peso (CLP) currency
 * @param amount - The amount to format
 * @returns Formatted currency string (e.g., "$12.350")
 */
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

/**
 * Format a date string to Chilean locale format
 * @param dateString - ISO date string or Date object
 * @param options - Intl.DateTimeFormatOptions for customization
 * @returns Formatted date string (e.g., "15 de enero de 2024")
 */
export const formatDate = (
  dateString: string | Date,
  options?: Intl.DateTimeFormatOptions
): string => {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  
  const defaultOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...options
  };

  return new Intl.DateTimeFormat('es-CL', defaultOptions).format(date);
};

/**
 * Format a date string to short Chilean locale format
 * @param dateString - ISO date string or Date object
 * @returns Formatted short date string (e.g., "15/01/2024")
 */
export const formatDateShort = (dateString: string | Date): string => {
  return formatDate(dateString, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

/**
 * Format a date string to include time
 * @param dateString - ISO date string or Date object
 * @returns Formatted date with time (e.g., "15 de enero de 2024, 14:30")
 */
export const formatDateTime = (dateString: string | Date): string => {
  const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
  
  return new Intl.DateTimeFormat('es-CL', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};
